import { useMemo } from 'react'

import { ALL_TIME_RANGE_QUERY } from 'consts/filterOptions'
import type { Providers } from 'generated/graphql'
import { SparklineIntervalTimeUnitEnum, SparklineIntervalTypeEnum } from 'generated/graphql'
import { useTerritoryQueries } from 'hooks-queries/territory'
import { useTranslate } from 'lib/tolgee'
import { getTimeUnitInterval } from 'pages/Brand/utils/getTimeUnitInterval'
import { TOLGEE_GLOBALS } from 'utils'
import { getCustomTimeDifference } from 'utils/getCustomTimeDifference'
import { convertTimeUnit } from 'utils/getCustomTimeUnitInterval'
import getUploadDateByValue from 'utils/getUploadDateByValue'
import { PROVIDER_BY_KEY } from 'utils/providersUtils/consts'
import { getFormatedDateVariablesForTerritory } from 'utils/territoryUtils'

import { GET_BRAND_OVERVIEW_CARDS_ERROR_LOG } from './use-brand-overview-cards-data.consts'
import type {
  IUseBrandOverviewCardsDataParams,
  IUseBrandOverviewCardsDataReturn,
} from './use-brand-overview-cards-data.types'
import type { ICreatorsCardData, IVideosCardData } from '../../brands.types'

export const useBrandOverviewCardsData = ({
  territoryId,
  providers,
  uploadDateParam,
  includeCreators,
}: IUseBrandOverviewCardsDataParams): IUseBrandOverviewCardsDataReturn => {
  const t = useTranslate()

  const mappedProviders = useMemo(
    () => providers.map(p => PROVIDER_BY_KEY[p]).filter((p): p is Providers => p !== undefined),
    [providers]
  )

  const queryVariables = useMemo(() => {
    const isCustomDate = uploadDateParam?.includes('custom')
    const isAllTime = uploadDateParam === 'all'
    const is24h = uploadDateParam?.includes('24h')
    const dateValue = getFormatedDateVariablesForTerritory(uploadDateParam)
    const difference = getCustomTimeDifference(uploadDateParam)

    const getIntervalTimeUnit = () => {
      if (isCustomDate) return difference > 30 ? 'month' : 'day'
      if (isAllTime) return SparklineIntervalTimeUnitEnum.Quarter
      if (is24h) return SparklineIntervalTimeUnitEnum.Day
      if (uploadDateParam === '3') return 'month'
      return getTimeUnitInterval(uploadDateParam)
    }

    const timeUnit = getIntervalTimeUnit()

    const baseVars = {
      id: territoryId,
      providers: mappedProviders,
      interval: { type: SparklineIntervalTypeEnum.Calendar, timeUnit: convertTimeUnit(timeUnit) },
      includeCreators,
    }

    if (isAllTime) return { ...baseVars, publishedAt: ALL_TIME_RANGE_QUERY, timeRange: null }
    if (isCustomDate) return { ...baseVars, publishedAt: getUploadDateByValue(uploadDateParam), timeRange: null }

    return { ...baseVars, timeRange: dateValue }
  }, [territoryId, mappedProviders, uploadDateParam, includeCreators])

  const { data, loading, error, refetch } = useTerritoryQueries({
    queryName: 'getBrandOverviewCardsData',
    errorLog: GET_BRAND_OVERVIEW_CARDS_ERROR_LOG,
    queryVariables,
    options: {
      skip: !territoryId,
      fetchPolicy: 'cache-and-network',
    },
  })

  const territory = data?.getTerritory

  const hasData = territory !== undefined
  const isLoading = (loading || !territoryId) && !hasData
  const hasError = !isLoading && Boolean(error)

  const videosCardData: IVideosCardData = useMemo(() => {
    const totalVideos = territory?.territoryPerformance?.videosPerPeriod?.total ?? 0
    const thumbnail = territory?.territoryMostEngaged?.posts?.[0]?.thumbnail ?? ''

    return {
      total: totalVideos,
      thumbnail,
      labelText: t(TOLGEE_GLOBALS.videos),
      loading: isLoading,
      error: hasError,
      refetch,
    }
  }, [territory, isLoading, hasError, refetch, t])

  const creatorsCardData: ICreatorsCardData = useMemo(() => {
    const totalCreators = territory?.territoryPerformance?.totalCreators ?? 0
    const post = territory?.territoryMostEngaged?.posts?.[0]
    const thumbnail = (post?.creator && 'thumbnail' in post.creator ? post.creator.thumbnail : '') ?? ''

    return {
      total: totalCreators,
      thumbnail,
      labelText: t(TOLGEE_GLOBALS.creators),
      loading: isLoading,
      error: hasError,
      refetch,
    }
  }, [territory, isLoading, hasError, refetch, t])

  return {
    videosCardData,
    creatorsCardData,
    loading: isLoading,
    error: hasError,
    refetch,
  }
}
